// src/pages/Enseignants/PassagesGrades/components/GradeAdherentCard.jsx

import React, { useEffect, useMemo, useState } from "react";
import {
  FaCheck,
  FaEdit,
  FaGraduationCap,
  FaTimes,
  FaUserClock,
} from "react-icons/fa";

import {
  calculateAge,
  formatDateFr,
  getAdherentFullName,
  getAdherentPhotoUrl,
  getGroupLabel,
  getInitials,
  gradeResultLabel,
  gradeResultTone,
  parseGradePassage,
} from "../utils/gradePassageUtils";

const GradeAdherentCard = ({
  adherent,
  saving,
  onQuickGrade,
  onOpenDrawer,
}) => {
  const [imageError, setImageError] = useState(false);

  const gradeInfo = useMemo(
    () => parseGradePassage(adherent?.notes_enseignant),
    [adherent]
  );

  const photoUrl = useMemo(() => getAdherentPhotoUrl(adherent), [adherent]);

  useEffect(() => {
    setImageError(false);
  }, [photoUrl]);

  const fullName = getAdherentFullName(adherent);
  const age = calculateAge(adherent?.date_naissance);
  const tone = gradeResultTone(gradeInfo.result);
  const groups = adherent?.Groups || [];

  const statusClass = (() => {
    if (!gradeInfo.isGraded) {
      return {
        card: "border-amber-200 dark:border-amber-500/30",
        badge:
          "bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300",
      };
    }

    if (tone === "failed") {
      return {
        card: "border-rose-200 dark:border-rose-500/30",
        badge:
          "bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-300",
      };
    }

    return {
      card: "border-emerald-200 dark:border-emerald-500/30",
      badge:
        "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300",
      };
  })();

  return (
    <article
      className={`group relative flex h-full flex-col overflow-hidden rounded-[28px] border bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:bg-slate-900 ${statusClass.card}`}
    >
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-500 via-violet-500 to-fuchsia-500" />

      <button
        type="button"
        onClick={() => onOpenDrawer(adherent)}
        className="flex flex-1 flex-col p-4 text-left"
      >
        {/* Header card vertical, identique mobile / desktop */}
        <div className="flex flex-col items-center text-center">
          <div className="relative h-24 w-24 overflow-hidden rounded-[26px] bg-slate-100 shadow-sm dark:bg-slate-800">
            {photoUrl && !imageError ? (
              <img
                src={photoUrl}
                alt={fullName}
                className="h-full w-full object-cover"
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-slate-900 text-2xl font-bold text-white dark:bg-white dark:text-slate-950">
                {getInitials(adherent)}
              </div>
            )}
          </div>

          <div className="mt-3 flex flex-col items-center gap-2">
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold ${statusClass.badge}`}
            >
              {gradeInfo.isGraded ? <FaGraduationCap /> : <FaUserClock />}
              {gradeResultLabel(gradeInfo)}
            </span>

            <div>
              <h3 className="line-clamp-2 text-lg font-bold leading-tight text-slate-900 dark:text-white">
                {fullName}
              </h3>

              <p className="mt-1 text-sm font-medium text-slate-500 dark:text-slate-400">
                {age !== null ? `${age} ans` : "Âge non renseigné"}
                {adherent?.grade ? ` · ${adherent.grade}` : ""}
              </p>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap justify-center gap-1.5">
            {groups.slice(0, 4).map((group) => (
              <span
                key={group.id || getGroupLabel(group)}
                className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300"
              >
                {getGroupLabel(group)}
              </span>
            ))}
          </div>
        </div>

        {/* Bloc passage */}
        <div className="mt-4 flex-1 rounded-2xl bg-slate-50 p-4 dark:bg-slate-950/70">
          {gradeInfo.isGraded ? (
            <div className="space-y-1 text-sm">
              <p className="font-bold text-slate-900 dark:text-white">
                Passage noté le {formatDateFr(gradeInfo.date)}
              </p>

              <p className="text-sm text-slate-500 dark:text-slate-400">
                Par {gradeInfo.teacher || "enseignant non renseigné"}
              </p>

              {gradeInfo.comment ? (
                <p className="line-clamp-2 text-sm text-slate-500 dark:text-slate-400">
                  {gradeInfo.comment}
                </p>
              ) : null}
            </div>
          ) : (
            <div className="space-y-1 text-sm">
              <p className="font-bold text-slate-900 dark:text-white">
                Pas encore noté
              </p>

              <p className="text-sm text-slate-500 dark:text-slate-400">
                Cliquez sur un bouton pour enregistrer le passage d’aujourd’hui.
              </p>
            </div>
          )}
        </div>
      </button>

      {/* Actions */}
      <div className="grid grid-cols-2 gap-2 border-t border-slate-100 p-3 dark:border-slate-800">
        <button
          type="button"
          disabled={saving}
          onClick={(event) => {
            event.stopPropagation();
            onQuickGrade(adherent, "Obtenu");
          }}
          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-3 py-2.5 text-xs font-bold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <FaCheck />
          Obtenu
        </button>

        <button
          type="button"
          disabled={saving}
          onClick={(event) => {
            event.stopPropagation();
            onQuickGrade(adherent, "Non obtenu");
          }}
          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-rose-600 px-3 py-2.5 text-xs font-bold text-white shadow-sm transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <FaTimes />
          Non obtenu
        </button>

        <button
          type="button"
          disabled={saving}
          onClick={(event) => {
            event.stopPropagation();
            onOpenDrawer(adherent);
          }}
          className="col-span-2 inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-xs font-bold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          <FaEdit />
          Modifier le texte / la date
        </button>
      </div>
    </article>
  );
};

export default GradeAdherentCard;